import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { combineLatest, EMPTY, filter, finalize, first, Observable, catchError, throwError, from, mergeMap } from "rxjs";
import { fromProgram, programActions, RootState } from "../../store";
import { AppQueryStrings, AppRoutes } from "../../enums";
import { resolvePaymentRequiredReason } from "./payment-required-reason";

/**
 * A 402 (no active plan) opens the plan picker page (Settings › Subscription › plans), with the reason, and flags the
 * paywall in the store so that every other page leads back to it (`PaymentRequiredGuard`) until a plan is active.
 */
@Injectable({ providedIn: "root" })
export class PaymentRequiredInterceptor implements HttpInterceptor {
  // Only ever wait for subscription info once: while it stays null every poll answers 402,
  // and a wait per response would pile up subscriptions that all resolve to the same page.
  private isAwaitingSubscriptionInfo = false;

  constructor(
    private readonly router: Router,
    private readonly store: Store<RootState>,
  ) {}

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === HttpStatusCode.PaymentRequired && !this.isAwaitingSubscriptionInfo) {
          this.isAwaitingSubscriptionInfo = true;
          combineLatest([this.store.select(fromProgram.selectSubscriptionInfo), this.store.select(fromProgram.selectProgram)])
            .pipe(
              // Filter before first: a 402 typically arrives while subscription info is still
              // null, and taking the first emission unconditionally would swallow it and never
              // show the paywall. Waiting instead opens the plan picker once the usage lands.
              filter(([subscriptionInfo, program]) => !!subscriptionInfo && !!program),
              first(),
              mergeMap(([subscriptionInfo, program]) => {
                const reason = resolvePaymentRequiredReason(error, req, subscriptionInfo);
                this.store.dispatch(programActions.setPaymentRequired({ reason }));
                if (this.isChoosingPlan()) return EMPTY;

                return from(
                  this.router.navigate([program.slug, AppRoutes.settings, AppRoutes.subscription, AppRoutes.plans], {
                    queryParams: { [AppQueryStrings.paymentRequiredReason]: reason },
                  }),
                );
              }),
              finalize(() => (this.isAwaitingSubscriptionInfo = false)),
            )
            .subscribe();
        }
        return throwError(() => error);
      }),
    );
  }

  // The plan picker is already open: the polls it makes while the merchant chooses keep answering 402
  private isChoosingPlan(): boolean {
    return this.router.url.split("?")[0].endsWith(`/${AppRoutes.settings}/${AppRoutes.subscription}/${AppRoutes.plans}`);
  }
}
